import type { RegisteredComponent } from "@builder.io/sdk-react/edge";
import { lazy } from "react";

import { MembershipPlan } from "~/lib/schemas/db-enums";

import { ICON_NAMES } from "../lib/icon-names";

const FeatureSection = lazy(() =>
  import("./ui/feature-section").then((module) => ({
    default: module.FeatureSection,
  })),
);
const Heading = lazy(() =>
  import("./ui/heading").then((module) => ({ default: module.Heading })),
);
const Prose = lazy(() =>
  import("./ui/prose").then((module) => ({ default: module.Prose })),
);
const Button = lazy(() =>
  import("./ui/button").then((module) => ({ default: module.Button })),
);
const Centered = lazy(() =>
  import("./helpers/centered").then((module) => ({ default: module.Centered })),
);
const PricingCard = lazy(() =>
  import("./ui/pricing-card").then((module) => ({
    default: module.PricingCard,
  })),
);
const PricingPlans = lazy(() =>
  import("./ui/pricing-plans").then((module) => ({
    default: module.PricingPlans,
  })),
);
const LivePricingPlans = lazy(() =>
  import("./ui/live-pricing-plans").then((module) => ({
    default: module.LivePricingPlans,
  })),
);
const PricingComparison = lazy(() =>
  import("./ui/pricing-comparison").then((module) => ({
    default: module.PricingComparison,
  })),
);
const ValueStack = lazy(() =>
  import("./ui/value-stack").then((module) => ({
    default: module.ValueStack,
  })),
);
const CredibilityBand = lazy(() =>
  import("./ui/credibility-band").then((module) => ({
    default: module.CredibilityBand,
  })),
);
const ProductShowcase = lazy(() =>
  import("./ui/product-showcase").then((module) => ({
    default: module.ProductShowcase,
  })),
);
const PathLadder = lazy(() =>
  import("./ui/path-ladder").then((module) => ({
    default: module.PathLadder,
  })),
);
const BuilderCard = lazy(() =>
  import("./helpers/builder-card").then((module) => ({
    default: module.BuilderCard,
  })),
);
const Container = lazy(() =>
  import("./helpers/container").then((module) => ({
    default: module.Container,
  })),
);
const Columns = lazy(() =>
  import("./helpers/columns").then((module) => ({ default: module.Columns })),
);
const CTASectionBuilder = lazy(() =>
  import("./ui/cta-banner").then((module) => ({ default: module.CtaBanner })),
);
const FeatureList = lazy(() =>
  import("./feature-list").then((module) => ({ default: module.FeatureList })),
);
const IconFeatureGrid = lazy(() =>
  import("./ui/icon-feature-grid").then((module) => ({
    default: module.IconFeatureGrid,
  })),
);
const AuroraBackground = lazy(() =>
  import("./ui/aurora-background").then((module) => ({
    default: module.AuroraBackground,
  })),
);
const MembershipPlanDetails = lazy(() =>
  import("./membership-plan-details").then((module) => ({
    default: module.MembershipPlanDetails,
  })),
);
const SavingsComparisonTable = lazy(() =>
  import("./savings-comparison-table").then((module) => ({
    default: module.SavingsComparisonTable,
  })),
);
const ImageWithCaption = lazy(() =>
  import("./ui/image-with-caption").then((module) => ({
    default: module.ImageWithCaption,
  })),
);
const Testimonials = lazy(() =>
  import("./ui/testimonials").then((module) => ({
    default: module.Testimonials,
  })),
);
const BuilderLink = lazy(() =>
  import("./helpers/builder-link").then((module) => ({
    default: module.BuilderLink,
  })),
);
const CcoCarousel = lazy(() =>
  import("./cco-carousel").then((module) => ({ default: module.CcoCarousel })),
);
const CCOList = lazy(() =>
  import("./cco-list").then((module) => ({ default: module.CCOList })),
);
const CountdownBar = lazy(() =>
  import("./ui/countdown-bar").then((module) => ({
    default: module.CountdownBar,
  })),
);
const TagList = lazy(() =>
  import("./ui/tag-list").then((module) => ({ default: module.TagList })),
);
const LabelList = lazy(() =>
  import("./ui/label-list").then((module) => ({ default: module.LabelList })),
);
const AuthorGrid = lazy(() =>
  import("./ui/author-grid").then((module) => ({ default: module.AuthorGrid })),
);
const TimeZoneOverview = lazy(() =>
  import("./ui/timezone-overview").then((module) => ({
    default: module.TimeZoneOverview,
  })),
);
const FullScreenCTA = lazy(() =>
  import("./ui/full-screen-cta").then((module) => ({
    default: module.FullScreenCTA,
  })),
);
const DailySummitAgenda = lazy(() =>
  import("./ui/daily-summit-agenda").then((module) => ({
    default: module.DailySummitAgenda,
  })),
);
const SummitRegistration = lazy(() =>
  import("./summit/summit-registration").then((module) => ({
    default: module.SummitRegistration,
  })),
);
const SummitRegistrationPopup = lazy(() =>
  import("./summit/summit-registration-popup").then((module) => ({
    default: module.SummitRegistrationPopup,
  })),
);
const SummitLiveStream = lazy(() =>
  import("./summit/summit-live-stream").then((module) => ({
    default: module.SummitLiveStream,
  })),
);
const DoubleImageCarousel = lazy(() =>
  import("./ui/double-image-carousel").then((module) => ({
    default: module.DoubleImageCarousel,
  })),
);
const AnimatedChevrons = lazy(() =>
  import("./icons/animated-chevrons").then((module) => ({
    default: module.AnimatedChevrons,
  })),
);
const CtaBanner = lazy(() =>
  import("./ui/cta-banner").then((module) => ({
    default: module.CtaBanner,
  })),
);
const FaqSection = lazy(() =>
  import("./ui/faq-section").then((module) => ({
    default: module.FaqSection,
  })),
);
const ProseBlock = lazy(() =>
  import("./ui/prose-block").then((module) => ({
    default: module.ProseBlock,
  })),
);
const PullQuote = lazy(() =>
  import("./ui/pull-quote").then((module) => ({
    default: module.PullQuote,
  })),
);
const FounderSection = lazy(() =>
  import("./ui/founder-section").then((module) => ({
    default: module.FounderSection,
  })),
);
const StoriesCarousel = lazy(() =>
  import("./stories-carousel").then((module) => ({
    default: module.StoriesCarousel,
  })),
);
const FeatureShowcase = lazy(() =>
  import("./ui/feature-showcase").then((module) => ({
    default: module.FeatureShowcase,
  })),
);
const CenteredIconSection = lazy(() =>
  import("./ui/centered-icon-section").then((module) => ({
    default: module.CenteredIconSection,
  })),
);
const ProcessSteps = lazy(() =>
  import("./ui/process-steps").then((module) => ({
    default: module.ProcessSteps,
  })),
);
const AgentShowcase = lazy(() =>
  import("./ui/agent-showcase").then((module) => ({
    default: module.AgentShowcase,
  })),
);
// Coaching OS showcase components — real product surfaces rendered from demo
// data (prop-only, edge-safe). See src/components/showcase/.
const ClientWorkspaceShowcase = lazy(() =>
  import("./showcase/client-workspace-showcase").then((module) => ({
    default: module.ClientWorkspaceShowcase,
  })),
);
const CoachClientsShowcase = lazy(() =>
  import("./showcase/coach-clients-showcase").then((module) => ({
    default: module.CoachClientsShowcase,
  })),
);
const GoalsShowcase = lazy(() =>
  import("./showcase/goals-showcase").then((module) => ({
    default: module.GoalsShowcase,
  })),
);
const SessionTimelineShowcase = lazy(() =>
  import("./showcase/session-timeline-showcase").then((module) => ({
    default: module.SessionTimelineShowcase,
  })),
);
const MessagingShowcase = lazy(() =>
  import("./showcase/messaging-showcase").then((module) => ({
    default: module.MessagingShowcase,
  })),
);
const ActionShowcase = lazy(() =>
  import("./showcase/action-showcase").then((module) => ({
    default: module.ActionShowcase,
  })),
);

// CIH (Coaching Intelligence Hub) components
const CihChallengeHero = lazy(() =>
  import("./cih/challenge-hero").then((module) => ({
    default: module.CihChallengeHero,
  })),
);
const CihSection = lazy(() =>
  import("./cih/section").then((module) => ({
    default: module.CihSection,
  })),
);
const CihSectionHeading = lazy(() =>
  import("./cih/section-heading").then((module) => ({
    default: module.CihSectionHeading,
  })),
);
const CihFaqSection = lazy(() =>
  import("./cih/faq-section").then((module) => ({
    default: module.CihFaqSection,
  })),
);
const QuickFactsGrid = lazy(() =>
  import("./cih/quick-facts-grid").then((module) => ({
    default: module.QuickFactsGrid,
  })),
);
const RelatedEntityPills = lazy(() =>
  import("./cih/related-entity-pills").then((module) => ({
    default: module.RelatedEntityPills,
  })),
);
const TrustSignalBadge = lazy(() =>
  import("./cih/trust-signal-badge").then((module) => ({
    default: module.TrustSignalBadge,
  })),
);
const HubPageHeader = lazy(() =>
  import("./feature/hub-page-header").then((module) => ({
    default: module.HubPageHeader,
  })),
);

export const configureBuilder = () => {
  if (typeof window === "undefined" || typeof parent !== "undefined") {
    return;
  }

  const baseSize = {
    xs: "20rem",
    sm: "24rem",
    md: "28rem",
    lg: "32rem",
    xl: "36rem",
    "2xl": "42rem",
    "3xl": "48rem",
    "4xl": "56rem",
    "5xl": "64rem",
    "6xl": "72rem",
    "7xl": "80rem",
    prose: "65ch",
  };

  const borderRadius = {
    DEFAULT: "0.25rem",
    none: "0",
    sm: "0.125rem",
    md: "0.375rem",
    lg: "0.5rem",
    xl: "0.75rem",
    "2xl": "1rem",
    "3xl": "1.5rem",
    full: "9999px",
  };

  const boxShadow = {
    DEFAULT: [
      "var(--tw-inset-shadow), 0 1px 3px 0 rgb(0 0 0 / 0.1)",
      "var(--tw-inset-shadow), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
    ],
    none: "0 0 rgb(0 0 0 / 0)",
    sm: "var(--tw-inset-shadow), 0 1px 2px 0 rgb(0 0 0 / 0.05)",
    md: [
      "var(--tw-inset-shadow), 0 4px 6px -1px rgb(0 0 0 / 0.1)",
      "var(--tw-inset-shadow), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
    ],
    lg: [
      "var(--tw-inset-shadow), 0 10px 15px -3px rgb(0 0 0 / 0.1)",
      "var(--tw-inset-shadow), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
    ],
    xl: [
      "var(--tw-inset-shadow), 0 20px 25px -5px rgb(0 0 0 / 0.1)",
      "var(--tw-inset-shadow), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
    ],
    "2xl": "var(--tw-inset-shadow), 0 25px 50px -12px rgb(0 0 0 / 0.25)",
    inner: "inset 0 2px 4px 0 rgb(0 0 0 / 0.05)",
  };

  import("@builder.io/sdk-react/edge").then((module) => {
    module.register("editor.settings", {
      hideHeatMap: true,
      hideFormComponents: true,
      hideDataTab: false,
      hideABTab: true,
      hidePageUrlEditor: false,
      hideTemplates: false,

      hideStyleTab: false,
      styleStrictMode: true,
      designTokens: {
        colors: ["brand", "violet", "orange", "gray"].flatMap((color) =>
          [
            "50",
            "100",
            "200",
            "300",
            "400",
            "500",
            "600",
            "700",
            "800",
            "900",
            "950",
          ].map((brightness) => ({
            name: `${color}-${brightness}`,
            value: `var(--${color}-${brightness})`,
          })),
        ),
        spacing: [
          ...[2, 4, 8, 16, 24, 32, 48, 64, 96, 128].map((size) => ({
            name: `${size}`,
            value: `calc(var(--spacing) * ${size})`,
          })),
          ...Object.entries(baseSize).map(([name, value]) => ({
            name,
            value: value,
          })),
        ],
        fontFamily: [
          { name: "Heading", value: "var(--font-heading, sans-serif)" },
          { name: "Primary", value: "var(--font-sans, sans-serif)" },
        ],
        borderRadius: Object.entries(borderRadius).map(([name, value]) => ({
          name,
          value: value,
        })),
        boxShadow: Object.entries(boxShadow).map(([name, value]) => ({
          name,
          value: (Array.isArray(value) ? value : [value]).join(", "),
        })),
      },
    });
  });
};

/**
 * This array is used to integrate custom components within Builder.
 * https://www.builder.io/c/docs/custom-components-intro
 *
 * These components will be found the "Custom Components"
 * section of Builder's visual editor.
 * You can also turn on "components only mode" to limit
 * editing to only these components.
 * https://www.builder.io/c/docs/guides/components-only-mode
 */
export const CUSTOM_COMPONENTS: RegisteredComponent[] = [
  {
    component: Container,
    name: "Container",
    canHaveChildren: true,
    inputs: [
      {
        name: "pagePadding",
        type: "boolean",
        required: false,
      },
      {
        name: "maxWidth",
        type: "string",
        enum: ["prose", "sm", "md", "lg", "xl", "2xl", "full"],
        required: false,
      },
    ],
  },
  {
    component: DoubleImageCarousel,
    name: "DoubleImageCarousel",
    friendlyName: "Double Image Carousel",
    canHaveChildren: false,
    inputs: [
      {
        name: "images",
        type: "list",
        required: true,
        subFields: [
          { name: "src", type: "file", required: true },
          { name: "alt", type: "string", required: false },
          { name: "href", type: "string", required: false },
        ],
      },
      { name: "className", type: "string", required: false },
      { name: "rowHeight", type: "number", required: false, defaultValue: 260 },
      { name: "gapPx", type: "number", required: false, defaultValue: 24 },
      {
        name: "speedPxPerSecond",
        type: "number",
        required: false,
        defaultValue: 30,
        helperText: "Pixels per second for constant scrolling speed",
      },
      {
        name: "topDirection",
        type: "string",
        enum: ["left", "right"],
        required: false,
        defaultValue: "left",
      },
      {
        name: "bottomDirection",
        type: "string",
        enum: ["left", "right"],
        required: false,
        defaultValue: "right",
      },
      {
        name: "pauseOnHover",
        type: "boolean",
        required: false,
        defaultValue: true,
      },
      {
        name: "maskEdges",
        type: "boolean",
        required: false,
        defaultValue: true,
      },
      {
        name: "maskBackgroundColor",
        type: "string",
        required: false,
        defaultValue: "var(--color-background)",
        helperText:
          "CSS color for the mask edges (e.g., #ffffff, hsl(0 0% 100%), etc.)",
      },
      {
        name: "rounded",
        type: "string",
        enum: ["none", "sm", "md", "lg", "xl", "2xl", "3xl", "full"],
        required: false,
        defaultValue: "xl",
      },
      {
        name: "imageFit",
        type: "string",
        enum: ["cover", "contain", "fill", "none", "scale-down"],
        required: false,
        defaultValue: "cover",
      },
      {
        name: "showSecondRow",
        type: "boolean",
        required: false,
        defaultValue: true,
        helperText: "Show or hide the second row of images",
      },
      {
        name: "randomizeOrder",
        type: "boolean",
        required: false,
        defaultValue: true,
        helperText: "Randomize the order of images in each row",
      },
    ],
  },
  {
    component: Columns,
    name: "Distributed Columns",
    canHaveChildren: true,
    inputs: [
      {
        name: "count",
        type: "string",
        enum: ["1", "2", "3", "4"],
        required: false,
      },
    ],
  },
  {
    component: FeatureList,
    name: "FeatureList",
    friendlyName: "Feature List",
    inputs: [
      {
        name: "features",
        type: "list",
        subFields: [
          { name: "feature", type: "string", required: true },
          { name: "description", type: "string", required: false },
        ],
        required: true,
      },
      {
        name: "size",
        type: "string",
        enum: ["default", "sm", "lg"],
        required: false,
        defaultValue: "default",
      },
      {
        name: "color",
        type: "string",
        enum: ["default", "vibrant"],
        required: false,
      },
    ],
  },
  {
    component: IconFeatureGrid,
    name: "IconFeatureGrid",
    friendlyName: "Icon Feature Grid",
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      { name: "description", type: "string", required: false },
      {
        name: "tone",
        type: "string",
        enum: ["muted", "plain", "dark"],
        defaultValue: "muted",
        required: false,
      },
      {
        name: "columns",
        type: "number",
        defaultValue: 3,
        helperText: "Choose 2, 3, or 4 columns for the grid",
        required: false,
      },
      {
        name: "items",
        type: "list",
        required: true,
        subFields: [
          { name: "title", type: "string", required: true },
          { name: "description", type: "string", required: false },
          {
            name: "iconName",
            type: "string",
            enum: [...ICON_NAMES],
            required: false,
            helperText:
              "Pick a preset icon or leave blank to use a number badge",
          },
          {
            name: "badgeNumber",
            type: "number",
            required: false,
            helperText: "Provide a number to display instead of an icon",
          },
        ],
      },
    ],
  },
  {
    component: LivePricingPlans,
    name: "LivePricingPlans",
    friendlyName: "Pricing Plans (live Stripe prices)",
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      { name: "description", type: "longText", required: false },
      {
        name: "tone",
        type: "string",
        enum: ["purple", "slate", "plain"],
        defaultValue: "purple",
        required: false,
      },
      {
        name: "plans",
        type: "list",
        required: true,
        helperText:
          "One card per plan. Prices, the yearly total and the saving all come from Stripe — a plan with no live price is hidden rather than shown with stale numbers.",
        subFields: [
          {
            name: "planKey",
            type: "string",
            enum: ["nova", "nova_pro", "marketing_suite"],
            required: true,
            helperText: "Which plan's live Stripe price fills this card",
          },
          {
            name: "name",
            type: "string",
            required: false,
            helperText: "Overrides the Stripe product name. Blank to use it.",
          },
          {
            name: "who",
            type: "string",
            required: false,
            helperText: "Short 'who it's for' line",
          },
          { name: "ctaText", type: "string", required: false },
          { name: "ctaHref", type: "string", required: false },
          {
            name: "badge",
            type: "string",
            required: false,
            helperText: 'Pill above the card (e.g. "Best value")',
          },
          { name: "featured", type: "boolean", defaultValue: false },
          {
            name: "features",
            type: "list",
            required: false,
            subFields: [
              { name: "text", type: "string", required: true },
              {
                name: "group",
                type: "string",
                required: false,
                helperText: 'Optional sub-heading (e.g. "Diagnose")',
              },
            ],
          },
        ],
      },
    ],
  },
  {
    component: PricingPlans,
    name: "PricingPlans",
    friendlyName: "Pricing Plans (manual prices — prefer the live version)",
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      { name: "description", type: "longText", required: false },
      {
        name: "monthlyLabel",
        type: "string",
        defaultValue: "Monthly",
        required: false,
      },
      {
        name: "annualLabel",
        type: "string",
        defaultValue: "Annual",
        required: false,
      },
      {
        name: "annualSavePill",
        type: "string",
        defaultValue: "Save 17%",
        required: false,
        helperText: "Savings pill on the annual toggle — blank to hide",
      },
      {
        name: "tone",
        type: "string",
        enum: ["purple", "slate", "plain"],
        defaultValue: "purple",
        required: false,
      },
      {
        name: "plans",
        type: "list",
        required: true,
        subFields: [
          { name: "name", type: "string", required: true },
          {
            name: "who",
            type: "string",
            required: false,
            helperText: "Short 'who it's for' line",
          },
          { name: "monthlyPrice", type: "string", required: true },
          {
            name: "annualPrice",
            type: "string",
            required: false,
            helperText: "Shown when Annual is selected; falls back to monthly",
          },
          {
            name: "priceSuffix",
            type: "string",
            required: false,
            helperText: 'e.g. "/mo" or "/forever"',
          },
          {
            name: "billedMonthly",
            type: "string",
            required: false,
            helperText: 'Caption under monthly price (e.g. "No card required")',
          },
          {
            name: "billedAnnual",
            type: "string",
            required: false,
            helperText:
              'Caption under annual price (e.g. "$498 billed annually")',
          },
          { name: "ctaText", type: "string", required: false },
          { name: "ctaHref", type: "string", required: false },
          {
            name: "badge",
            type: "string",
            required: false,
            helperText: 'Pill above the card (e.g. "Most popular")',
          },
          {
            name: "featured",
            type: "boolean",
            defaultValue: false,
            required: false,
            helperText: "Dark, elevated 'most popular' treatment",
          },
          {
            name: "inherits",
            type: "string",
            required: false,
            helperText: 'Italic carry-over (e.g. "Everything in Lite, plus —")',
          },
          {
            name: "features",
            type: "list",
            required: false,
            subFields: [
              { name: "text", type: "string", required: true },
              {
                name: "group",
                type: "string",
                required: false,
                helperText: "Sub-section heading (e.g. NOVA AI) — groups items",
              },
            ],
          },
        ],
      },
    ],
  },
  {
    component: PricingComparison,
    name: "PricingComparison",
    friendlyName: "Pricing Comparison Table (collapsible)",
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      {
        name: "featureColumnLabel",
        type: "string",
        defaultValue: "Feature",
        required: false,
      },
      {
        name: "showLabel",
        type: "string",
        defaultValue: "Show detailed comparison",
        required: false,
      },
      {
        name: "hideLabel",
        type: "string",
        defaultValue: "Hide detailed comparison",
        required: false,
      },
      {
        name: "defaultOpen",
        type: "boolean",
        defaultValue: false,
        required: false,
      },
      {
        name: "tone",
        type: "string",
        enum: ["purple", "slate", "plain"],
        defaultValue: "purple",
        required: false,
      },
      {
        name: "columns",
        type: "list",
        required: true,
        helperText: "Plan headers, left to right",
        subFields: [{ name: "label", type: "string", required: true }],
      },
      {
        name: "rows",
        type: "list",
        required: true,
        subFields: [
          {
            name: "label",
            type: "string",
            required: true,
            helperText: "Feature name, or category title when 'isCategory'",
          },
          {
            name: "isCategory",
            type: "boolean",
            defaultValue: false,
            required: false,
            helperText: "Render as a full-width category band",
          },
          {
            name: "cells",
            type: "list",
            required: false,
            helperText:
              'One per column. Use "✓", "—", plain text, or "*text*" to emphasize',
            subFields: [{ name: "value", type: "string", required: false }],
          },
        ],
      },
    ],
  },
  {
    component: ValueStack,
    name: "ValueStack",
    friendlyName: "Value Stack (what it replaces)",
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      { name: "description", type: "longText", required: false },
      {
        name: "footnote",
        type: "longText",
        required: false,
        helperText:
          "Closing line — wrap a fragment in *asterisks* to emphasize",
      },
      {
        name: "tone",
        type: "string",
        enum: ["purple", "slate", "plain"],
        defaultValue: "purple",
        required: false,
      },
      {
        name: "columns",
        type: "list",
        required: true,
        subFields: [
          { name: "title", type: "string", required: true },
          {
            name: "items",
            type: "list",
            required: false,
            subFields: [
              { name: "label", type: "string", required: true },
              { name: "amount", type: "string", required: false },
            ],
          },
          {
            name: "totalLabel",
            type: "string",
            required: false,
            helperText: 'e.g. "NOVA replaces it"',
          },
          {
            name: "totalValue",
            type: "string",
            required: false,
            helperText: 'e.g. "from $50/mo"',
          },
        ],
      },
    ],
  },
  {
    component: CredibilityBand,
    name: "CredibilityBand",
    friendlyName: "Credibility Band (dark)",
    inputs: [
      {
        name: "preHeadline",
        type: "string",
        required: false,
        helperText: "Small uppercase eyebrow",
      },
      { name: "headline", type: "string", required: false },
      { name: "description", type: "longText", required: false },
      { name: "ctaText", type: "string", required: false },
      { name: "ctaHref", type: "string", required: false },
    ],
  },
  {
    component: PricingCard,
    name: "PricingCard",
    canHaveChildren: true,
    inputs: [
      {
        name: "membershipPlan",
        type: "string",
        enum: Object.values(MembershipPlan),
        required: false,
      },
      { name: "price", type: "string", required: false },
      { name: "priceSuffix", type: "string", required: false },
      { name: "description", type: "string", required: false },
      { name: "highlighted", type: "boolean", defaultValue: false },
    ],
  },
  {
    component: Prose,
    name: "Prose",
    inputs: [
      {
        name: "content",
        type: "richText",
        defaultValue: "<p>This is some content</p>",
        required: true,
      },
      {
        name: "size",
        type: "string",
        enum: ["lg", "xl"],
        required: false,
      },
    ],
  },
  {
    component: BuilderCard,
    name: "Card",
    canHaveChildren: true,
    inputs: [
      {
        name: "title",
        type: "string",
        required: false,
      },
      {
        name: "description",
        type: "string",
        required: false,
      },
      {
        name: "fillHeight",
        type: "boolean",
        required: false,
      },
    ],
  },
  {
    component: CTASectionBuilder,
    name: "CTA Section",
    inputs: [
      {
        name: "headline",
        type: "string",
        required: false,
      },
      {
        name: "description",
        type: "string",
        required: false,
      },
      {
        name: "buttonHref",
        type: "string",
        required: false,
      },
      {
        name: "buttonText",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: BuilderLink,
    name: "Link",
    canHaveChildren: true,
    inputs: [
      {
        name: "href",
        type: "string",
        required: true,
      },
      {
        name: "target",
        type: "string",
        enum: ["_self", "_blank", "_parent", "_top"],
      },
    ],
  },
  {
    component: Button,
    name: "DCMButton",
    canHaveChildren: true,
    inputs: [
      {
        name: "variant",
        type: "string",
        defaultValue: "default",
        enum: [
          "default",
          "secondary",
          "fancy",
          "outline",
          "ghost",
          "flat",
          "pro",
        ],
        required: true,
      },
      {
        name: "size",
        type: "string",
        defaultValue: "default",
        enum: ["default", "sm", "lg", "xl"],
        required: true,
      },
      {
        name: "type",
        type: "string",
        enum: ["button", "link"],
        defaultValue: "button",
        required: false,
      },
    ],
  },
  {
    component: FeatureSection,
    name: "FeatureSection",
    inputs: [
      {
        name: "layout",
        type: "string",
        enum: ["left", "right"],
        required: true,
      },
      {
        name: "look",
        type: "string",
        enum: ["active", "inactive"],
        required: true,
      },
      {
        name: "preHeadline",
        type: "string",
        required: true,
      },
      {
        name: "headline",
        type: "string",
        required: true,
      },
      {
        name: "description",
        type: "string",
        required: true,
      },
    ],
  },
  {
    component: MembershipPlanDetails,
    name: "MembershipPlanDetails",
    friendlyName: "Plan Details Table (expandable)",
  },
  {
    component: Heading,
    name: "Heading",
    inputs: [
      {
        name: "className",
        type: "string",
      },
      {
        name: "preHeadline",
        type: "string",
      },
      {
        name: "headline",
        type: "string",
      },
      {
        name: "subheadline",
        type: "string",
      },
      {
        name: "isH1",
        type: "boolean",
        friendlyName: "Top-level heading? (H1)",
        defaultValue: false,
      },
      {
        name: "align",
        type: "string",
        enum: ["center", "left"],
        defaultValue: "center",
        required: false,
      },
      {
        name: "description",
        type: "string",
        required: false,
      },
      {
        name: "iconName",
        type: "string",
        enum: [...ICON_NAMES],
        required: false,
        helperText: "Pick a preset icon to display above the heading",
      },
    ],
  },
  {
    component: Centered,
    name: "Centered",
    canHaveChildren: true,
  },
  {
    component: AuroraBackground,
    name: "AuroraBackground",
    friendlyName: "Aurora Background",
    canHaveChildren: true,
  },
  {
    component: SavingsComparisonTable,
    name: "SavingsComparisonTable",
    description:
      "A table comparing software costs and savings across different membership plans",
    inputs: [
      {
        name: "className",
        type: "string",
      },
    ],
  },
  {
    component: ImageWithCaption,
    name: "ImageWithCaption",
    friendlyName: "Image with Caption",
    canHaveChildren: true,
    inputs: [
      {
        name: "caption",
        type: "string",
        required: true,
      },
    ],
  },
  {
    component: Testimonials,
    name: "Testimonials",
    friendlyName: "Testimonials",
    inputs: [
      {
        name: "testimonials",
        type: "list",
        subFields: [
          {
            name: "author",
            type: "string",
            required: true,
          },
          {
            name: "role",
            type: "string",
            required: false,
          },
          {
            name: "title",
            type: "string",
            required: false,
          },
          {
            name: "content",
            type: "richText",
            required: true,
          },
          {
            name: "setHTML",
            type: "boolean",
            defaultValue: true,
            hideFromUI: false,
          },
        ],
      },
    ],
  },
  {
    component: CcoCarousel,
    name: "CcoCarousel",
    friendlyName: "CCO Carousel",
    canHaveChildren: false,
  },
  {
    component: CCOList,
    name: "CCOList",
    friendlyName: "CCO List",
    canHaveChildren: false,
  },
  {
    component: CountdownBar,
    name: "CountdownBar",
    friendlyName: "Countdown Bar",
    canHaveChildren: false,
    noWrap: true,
    inputs: [
      {
        name: "text",
        type: "string",
        required: true,
        defaultValue: "Summit starts in:",
      },
      {
        name: "targetDate",
        type: "date",
        required: true,
      },
      {
        name: "ctaText",
        type: "string",
        required: true,
        defaultValue: "Register for free",
      },
      {
        name: "ctaLink",
        type: "string",
        required: true,
        defaultValue: "/register",
      },
      {
        name: "showCountdown",
        type: "boolean",
        required: false,
        defaultValue: true,
      },
      {
        name: "ctaVariant",
        type: "string",
        enum: ["default", "secondary", "fancy", "outline", "ghost"],
        required: false,
        defaultValue: "fancy",
      },
      {
        name: "ctaSize",
        type: "string",
        enum: ["default", "sm", "lg"],
        required: false,
        defaultValue: "default",
      },
    ],
  },
  {
    component: TagList,
    name: "TagList",
    friendlyName: "Tag List",
    canHaveChildren: false,
    inputs: [
      {
        name: "tags",
        type: "list",
        subFields: [
          { name: "id", type: "string", required: true },
          { name: "title", type: "string", required: true },
          {
            name: "property",
            type: "string",
            required: true,
          },
          {
            name: "icon",
            type: "string",
            required: false,
            enum: [...ICON_NAMES],
          },
        ],
        required: true,
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: LabelList,
    name: "LabelList",
    friendlyName: "Label List",
    canHaveChildren: false,
    inputs: [
      {
        name: "labels",
        type: "list",
        subFields: [
          { name: "id", type: "string", required: true },
          { name: "text", type: "string", required: true },
          {
            name: "variant",
            type: "string",
            enum: ["default", "secondary", "outline"],
            required: false,
          },
          {
            name: "size",
            type: "string",
            enum: ["sm", "md", "lg", "xl"],
            required: false,
          },
        ],
        required: true,
      },
      {
        name: "gap",
        type: "string",
        enum: ["sm", "md", "lg"],
        required: false,
        defaultValue: "md",
      },
      {
        name: "wrap",
        type: "boolean",
        required: false,
        defaultValue: true,
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: AuthorGrid,
    name: "AuthorGrid",
    friendlyName: "Author Grid",
    canHaveChildren: false,
    inputs: [
      {
        name: "authors",
        type: "list",
        subFields: [
          { name: "id", type: "string", required: true },
          { name: "name", type: "string", required: true },
          { name: "image", type: "file", required: true },
          { name: "title", type: "string", required: false },
          { name: "subtitle", type: "string", required: false },
          { name: "mainText", type: "richText", required: false },
        ],
        required: true,
      },
      {
        name: "columns",
        type: "string",
        enum: ["1", "2", "3", "4"],
        defaultValue: "3",
        required: false,
      },
      {
        name: "gap",
        type: "string",
        enum: ["sm", "md", "lg"],
        defaultValue: "md",
        required: false,
      },
      {
        name: "imageSize",
        type: "string",
        enum: ["sm", "md", "lg"],
        defaultValue: "md",
        required: false,
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: TimeZoneOverview,
    name: "TimeZoneOverview",
    friendlyName: "Time Zone Overview",
    canHaveChildren: false,
    inputs: [
      {
        name: "title",
        type: "string",
        defaultValue: "Attend free via Zoom",
        required: false,
      },
      {
        name: "dateRange",
        type: "string",
        defaultValue: "Friday, 25th - Sunday, 27th July 2025",
        required: false,
      },
      {
        name: "timeZones",
        type: "list",
        subFields: [
          {
            name: "flagImage",
            type: "string",
            required: true,
            defaultValue: "us.svg",
          },
          { name: "timeRange", type: "string", required: true },
          { name: "city", type: "string", required: true },
        ],
        required: true,
        defaultValue: [
          {
            flagImage: "us.svg",
            timeRange: "8am - 12pm",
            city: "Los Angeles",
          },
          {
            flagImage: "us.svg",
            timeRange: "11am - 3pm",
            city: "New York",
          },
          {
            flagImage: "gb.svg",
            timeRange: "4pm - 8pm",
            city: "London",
          },
          {
            flagImage: "ae.svg",
            timeRange: "7pm - 11pm",
            city: "Dubai",
          },
          {
            flagImage: "in.svg",
            timeRange: "8:30pm - 12:30am (+1)",
            city: "Delhi",
          },
        ],
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: FullScreenCTA,
    name: "FullScreenCTA",
    friendlyName: "Full Screen CTA",
    canHaveChildren: true,
    inputs: [
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
    defaultChildren: [
      {
        "@type": "@builder.io/sdk:Element",
        "@version": 2,
        id: "cta-heading",
        component: {
          name: "Heading",
          options: {
            preHeadline: "Don't Miss Out",
            headline: "Ready to Transform Your Business?",
            subheadline:
              "Join thousands of entrepreneurs who have accelerated their growth with our proven strategies.",
          },
        },
      },
      {
        "@type": "@builder.io/sdk:Element",
        "@version": 2,
        id: "cta-button",
        component: {
          name: "DCMButton",
          options: {
            variant: "primary",
            size: "lg",
          },
        },
        children: [
          {
            "@type": "@builder.io/sdk:Element",
            "@version": 2,
            component: {
              name: "Text",
              options: {
                text: "Get Started Today",
              },
            },
          },
        ],
      },
    ],
  },
  {
    component: DailySummitAgenda,
    name: "DailySummitAgenda",
    friendlyName: "Daily Summit Agenda",
    canHaveChildren: false,
    inputs: [
      {
        name: "title",
        type: "string",
        defaultValue: "Summit Agenda",
        required: false,
      },
      {
        name: "showTimes",
        type: "boolean",
        defaultValue: false,
        required: false,
      },
      {
        name: "description",
        type: "string",
        required: false,
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: SummitRegistration,
    name: "SummitRegistration",
    friendlyName: "Summit Registration",
    canHaveChildren: false,
    inputs: [
      {
        name: "redirectUrl",
        type: "string",
        friendlyName: "Redirect URL (after registration)",
        required: false,
        helperText:
          "URL to redirect users after successful registration. Leave empty for confirmation message.",
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: SummitRegistrationPopup,
    name: "SummitRegistrationPopup",
    friendlyName: "Summit Registration Popup",
    canHaveChildren: false,
    inputs: [
      {
        name: "redirectUrl",
        type: "string",
        friendlyName: "Redirect URL (after registration)",
        required: false,
        helperText:
          "URL to redirect users after successful registration. Leave empty for confirmation message.",
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: AnimatedChevrons,
    name: "AnimatedChevrons",
    friendlyName: "Animated Chevrons",
    canHaveChildren: false,
    inputs: [
      {
        name: "size",
        type: "string",
        enum: ["sm", "default", "lg", "xl"],
        defaultValue: "default",
        required: false,
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: SummitLiveStream,
    name: "SummitLiveStream",
    friendlyName: "Summit Live Stream",
    canHaveChildren: false,
    inputs: [
      {
        name: "overrideVideoId",
        type: "string",
        friendlyName: "Override Video ID",
        required: false,
        helperText:
          "YouTube video ID for testing/rehearsals. Leave empty to auto-detect from live sessions.",
      },
      {
        name: "className",
        type: "string",
        required: false,
      },
    ],
  },
  {
    component: StoriesCarousel,
    name: "StoriesCarousel",
    friendlyName: "Stories Carousel",
    canHaveChildren: false,
  },
  {
    component: FaqSection,
    name: "FaqSection",
    friendlyName: "FAQ Section",
    inputs: [
      { name: "headline", type: "string", required: false },
      { name: "description", type: "string", required: false },
      {
        name: "items",
        type: "list",
        required: true,
        subFields: [
          { name: "question", type: "string", required: true },
          { name: "answer", type: "longText", required: true },
        ],
      },
      { name: "className", type: "string", required: false },
    ],
  },
  {
    component: CtaBanner,
    name: "CtaBanner",
    friendlyName: "CTA Banner",
    inputs: [
      { name: "headline", type: "string", required: false },
      { name: "description", type: "string", required: false },
      { name: "buttonText", type: "string", required: false },
      { name: "buttonHref", type: "string", required: false },
      {
        name: "buttonVariant",
        type: "string",
        enum: ["fancy", "default", "primary"],
        defaultValue: "fancy",
        required: false,
      },
      { name: "className", type: "string", required: false },
    ],
  },
  {
    component: ProseBlock,
    name: "ProseBlock",
    friendlyName: "Prose Block (SEO)",
    inputs: [
      { name: "headline", type: "string", required: false },
      { name: "content", type: "richText", required: true },
      {
        name: "tone",
        type: "string",
        enum: ["default", "purple", "inverted"],
        defaultValue: "default",
        required: false,
        helperText:
          "Background treatment — alternate these to give stacked sections rhythm",
      },
      { name: "buttonText", type: "string", required: false },
      { name: "buttonHref", type: "string", required: false },
      { name: "className", type: "string", required: false },
    ],
  },
  {
    component: FounderSection,
    name: "FounderSection",
    friendlyName: "Founder Section",
    inputs: [
      {
        name: "eyebrow",
        type: "string",
        required: false,
        helperText: "Small uppercase label above the title (e.g. Our Founder)",
      },
      { name: "title", type: "string", required: false },
      { name: "bio", type: "richText", required: true },
      {
        name: "imageUrl",
        type: "file",
        required: false,
        allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
        helperText: "Portrait — rendered as a circle",
      },
      {
        name: "name",
        type: "string",
        required: false,
        helperText: "Caption shown beneath the portrait",
      },
      { name: "role", type: "string", required: false },
      {
        name: "imageSide",
        type: "string",
        enum: ["left", "right"],
        defaultValue: "left",
        required: false,
      },
      {
        name: "tone",
        type: "string",
        enum: ["default", "purple", "inverted"],
        defaultValue: "default",
        required: false,
        helperText: "Background treatment — match it to surrounding sections",
      },
      { name: "className", type: "string", required: false },
    ],
  },
  {
    component: PullQuote,
    name: "PullQuote",
    friendlyName: "Pull Quote",
    inputs: [
      { name: "quote", type: "longText", required: true },
      { name: "attributionName", type: "string", required: false },
      { name: "attributionRole", type: "string", required: false },
      {
        name: "align",
        type: "string",
        enum: ["center", "left"],
        defaultValue: "center",
        required: false,
      },
      { name: "className", type: "string", required: false },
    ],
  },
  {
    component: FeatureShowcase,
    name: "FeatureShowcase",
    friendlyName: "Feature Showcase",
    canHaveChildren: true,
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      { name: "description", type: "longText", required: false },
      { name: "ctaText", type: "string", required: false },
      { name: "ctaHref", type: "string", required: false },
      {
        name: "reverse",
        type: "boolean",
        defaultValue: false,
        required: false,
        helperText: "Swap text and visual columns for alternating rhythm",
      },
      { name: "className", type: "string", required: false },
    ],
  },
  {
    component: CenteredIconSection,
    name: "CenteredIconSection",
    friendlyName: "Centered Icon Section",
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      { name: "description", type: "longText", required: false },
      {
        name: "items",
        type: "list",
        required: true,
        subFields: [
          {
            name: "iconName",
            type: "string",
            enum: [...ICON_NAMES],
            required: true,
            helperText: "Icon displayed above the label",
          },
          { name: "label", type: "string", required: true },
          { name: "description", type: "string", required: true },
        ],
      },
      {
        name: "tone",
        type: "string",
        enum: ["purple", "slate", "plain"],
        defaultValue: "purple",
        required: false,
      },
      {
        name: "columns",
        type: "number",
        defaultValue: 3,
        required: false,
        helperText: "2, 3, or 4 columns",
      },
      { name: "className", type: "string", required: false },
    ],
  },
  {
    component: PathLadder,
    name: "PathLadder",
    friendlyName: "Path Ladder (choose your tier)",
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      { name: "description", type: "longText", required: false },
      {
        name: "tone",
        type: "string",
        enum: ["purple", "slate", "plain"],
        defaultValue: "purple",
        required: false,
      },
      {
        name: "steps",
        type: "list",
        required: true,
        subFields: [
          {
            name: "eyebrow",
            type: "string",
            required: false,
            helperText: 'Label above the title (e.g. "Step 1 · Free")',
          },
          { name: "title", type: "string", required: true },
          { name: "description", type: "longText", required: false },
          {
            name: "items",
            type: "list",
            required: false,
            subFields: [{ name: "text", type: "string", required: true }],
          },
          { name: "ctaText", type: "string", required: false },
          { name: "ctaHref", type: "string", required: false },
          {
            name: "featured",
            type: "boolean",
            defaultValue: false,
            required: false,
            helperText: "Elevated, ringed treatment for the emphasized rung",
          },
        ],
      },
    ],
  },
  {
    component: ProcessSteps,
    name: "ProcessSteps",
    friendlyName: "Process Steps",
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      { name: "description", type: "longText", required: false },
      {
        name: "tone",
        type: "string",
        enum: ["purple", "slate", "plain"],
        defaultValue: "purple",
        required: false,
      },
      {
        name: "steps",
        type: "list",
        required: true,
        subFields: [
          { name: "title", type: "string", required: true },
          { name: "description", type: "longText", required: false },
          {
            name: "iconName",
            type: "string",
            enum: [...ICON_NAMES],
            required: false,
            helperText: "Icon shown in the step badge — blank shows the number",
          },
          {
            name: "badge",
            type: "string",
            required: false,
            helperText:
              'Optional pill under the title (e.g. "Live", "Coming soon")',
          },
        ],
      },
    ],
  },
  {
    component: AgentShowcase,
    name: "AgentShowcase",
    friendlyName: "Agent Showcase (interactive)",
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      { name: "description", type: "longText", required: false },
      {
        name: "exploreHint",
        type: "string",
        required: false,
        defaultValue: "Click to explore",
        helperText: "Text on the pulsing nudge that invites clicking around",
      },
      {
        name: "tone",
        type: "string",
        enum: ["purple", "slate", "plain"],
        defaultValue: "purple",
        required: false,
      },
      {
        name: "slides",
        type: "list",
        required: true,
        subFields: [
          {
            name: "label",
            type: "string",
            required: true,
            helperText: "Sidebar label + screen title",
          },
          {
            name: "caption",
            type: "longText",
            required: true,
            helperText: "One line on what the agent does on this screen",
          },
          {
            name: "messages",
            type: "list",
            required: false,
            helperText: "Imagined NOVA conversation shown in the stage",
            subFields: [
              {
                name: "role",
                type: "string",
                enum: ["user", "nova"],
                defaultValue: "nova",
                required: true,
              },
              { name: "text", type: "longText", required: true },
            ],
          },
          {
            name: "image",
            type: "file",
            required: false,
            allowedFileTypes: ["jpeg", "jpg", "png", "webp", "gif"],
            helperText: "Screenshot — used instead of the conversation",
          },
          {
            name: "iconName",
            type: "string",
            enum: [...ICON_NAMES],
            required: false,
          },
          {
            name: "group",
            type: "string",
            required: false,
            helperText: "Optional sidebar grouping (e.g. Diagnose)",
          },
        ],
      },
    ],
  },
  {
    component: ProductShowcase,
    name: "ProductShowcase",
    friendlyName: "Product Showcase (video / annotated)",
    canHaveChildren: true,
    inputs: [
      { name: "preHeadline", type: "string", required: false },
      { name: "headline", type: "string", required: false },
      {
        name: "isH1",
        type: "boolean",
        friendlyName: "Top-level heading? (H1)",
        defaultValue: false,
      },
      { name: "description", type: "longText", required: false },
      {
        name: "videoUrl",
        type: "string",
        required: false,
        helperText:
          "YouTube / Vimeo / mp4 URL. When set, a video shows instead of the image",
      },
      {
        name: "image",
        type: "file",
        required: false,
        allowedFileTypes: ["jpeg", "jpg", "png", "webp"],
        helperText: "Screenshot for annotated mode",
      },
      {
        name: "aspect",
        type: "string",
        enum: ["16/9", "16/10", "4/3"],
        defaultValue: "16/9",
        required: false,
      },
      {
        name: "tone",
        type: "string",
        enum: ["purple", "slate", "plain"],
        defaultValue: "purple",
        required: false,
      },
      {
        name: "hotspots",
        type: "list",
        required: false,
        helperText: "Numbered callouts on the screenshot (annotated mode)",
        subFields: [
          {
            name: "x",
            type: "number",
            required: true,
            helperText: "Horizontal % (0–100)",
          },
          {
            name: "y",
            type: "number",
            required: true,
            helperText: "Vertical % (0–100)",
          },
          { name: "label", type: "string", required: true },
          { name: "description", type: "longText", required: false },
        ],
      },
    ],
  },
  // ─── Coaching OS showcase (real product surfaces from demo data) ──────────
  {
    component: ClientWorkspaceShowcase,
    name: "ClientWorkspaceShowcase",
    friendlyName: "Showcase — Client Workspace (Hearth)",
    canHaveChildren: false,
  },
  {
    component: CoachClientsShowcase,
    name: "CoachClientsShowcase",
    friendlyName: "Showcase — Coach Clients",
    canHaveChildren: false,
  },
  {
    component: GoalsShowcase,
    name: "GoalsShowcase",
    friendlyName: "Showcase — Goals / Life Vision",
    canHaveChildren: false,
  },
  {
    component: SessionTimelineShowcase,
    name: "SessionTimelineShowcase",
    friendlyName: "Showcase — Session Timeline",
    canHaveChildren: false,
  },
  {
    component: MessagingShowcase,
    name: "MessagingShowcase",
    friendlyName: "Showcase — Messaging",
    canHaveChildren: false,
  },
  {
    component: ActionShowcase,
    name: "ActionShowcase",
    friendlyName: "Showcase — Action Items",
    canHaveChildren: false,
  },

  // ─── CIH (Coaching Intelligence Hub) ─────────────────────────────────────
  {
    component: CihChallengeHero,
    name: "CihChallengeHero",
    friendlyName: "CIH Challenge Hero",
    canHaveChildren: false,
    inputs: [
      {
        name: "kicker",
        type: "string",
        required: true,
        defaultValue: "Coaching Intelligence",
        helperText: "Small uppercase label (e.g., Coaching Intelligence)",
      },
      {
        name: "title",
        type: "string",
        required: true,
        defaultValue: "Page title",
        helperText: "Gradient text headline",
      },
      {
        name: "lede",
        type: "longText",
        required: false,
        helperText: "Optional descriptive paragraph below the title",
      },
    ],
  },
  {
    component: HubPageHeader,
    name: "HubPageHeader",
    friendlyName: "Hub Page Header",
    canHaveChildren: true,
    inputs: [
      {
        name: "heading",
        type: "string",
        required: true,
        helperText: "Page heading",
      },
      {
        name: "subheading",
        type: "string",
        required: false,
      },
      {
        name: "description",
        type: "longText",
        required: false,
      },
      {
        name: "accentColorClass",
        type: "string",
        required: false,
        defaultValue: "bg-purple-500",
        helperText: "Tailwind bg color class for the decorative blur accent",
      },
      {
        name: "ctaLabel",
        type: "string",
        required: false,
        helperText: "Button text (shown only if both label and href are set)",
      },
      {
        name: "ctaHref",
        type: "string",
        required: false,
        helperText: "Button link destination",
      },
    ],
  },
  {
    component: CihSection,
    name: "CihSection",
    friendlyName: "CIH Section",
    canHaveChildren: true,
    inputs: [
      {
        name: "variant",
        type: "string",
        enum: ["white", "muted"],
        defaultValue: "white",
        required: true,
        helperText: "Background color — white or muted gray",
      },
      { name: "className", type: "string", required: false },
    ],
  },
  {
    component: CihSectionHeading,
    name: "CihSectionHeading",
    friendlyName: "CIH Section Heading",
    inputs: [
      {
        name: "heading",
        type: "string",
        required: true,
        helperText: "Main section heading text",
      },
      {
        name: "eyebrow",
        type: "string",
        required: false,
        helperText: "Small uppercase label above the heading",
      },
      { name: "className", type: "string", required: false },
    ],
  },
  {
    component: CihFaqSection,
    name: "CihFaqSection",
    friendlyName: "CIH FAQ Accordion",
    inputs: [
      {
        name: "faqs",
        type: "list",
        required: true,
        subFields: [
          { name: "question", type: "string", required: true },
          { name: "answer", type: "longText", required: true },
        ],
        helperText: "FAQ items rendered as numbered reference rows",
      },
      {
        name: "sectionLabel",
        type: "string",
        required: false,
        defaultValue: "Frequently asked",
      },
      {
        name: "heading",
        type: "string",
        required: false,
        defaultValue: "Questions",
      },
    ],
  },
  {
    component: QuickFactsGrid,
    name: "QuickFactsGrid",
    friendlyName: "Quick Facts Grid",
    inputs: [
      {
        name: "facts",
        type: "list",
        required: true,
        subFields: [
          {
            name: "label",
            type: "string",
            required: true,
            helperText: "Uppercase label (e.g., Duration, Tuition)",
          },
          {
            name: "value",
            type: "string",
            required: true,
            helperText: "Fact value displayed below the label",
          },
          {
            name: "href",
            type: "string",
            required: false,
            helperText: "Optional link — value renders as purple link text",
          },
        ],
      },
    ],
  },
  {
    component: RelatedEntityPills,
    name: "RelatedEntityPills",
    friendlyName: "Related Entity Pills",
    inputs: [
      {
        name: "heading",
        type: "string",
        required: true,
        helperText: "Section heading above the pills",
      },
      {
        name: "eyebrow",
        type: "string",
        required: false,
        helperText: "Small uppercase label above the heading",
      },
      {
        name: "items",
        type: "list",
        required: true,
        subFields: [
          { name: "label", type: "string", required: true },
          { name: "href", type: "string", required: true },
        ],
        helperText: "Pill-shaped links to related pages",
      },
    ],
  },
  {
    component: TrustSignalBadge,
    name: "TrustSignalBadge",
    friendlyName: "Trust Signal Badge",
    inputs: [
      {
        name: "reviewerName",
        type: "string",
        required: true,
        helperText: "Name of the reviewer (badge hidden when empty)",
      },
      {
        name: "reviewerCredentials",
        type: "string",
        required: false,
        helperText: "Credentials shown after the name (e.g., PCC, LCSW)",
      },
      {
        name: "reviewerUrl",
        type: "string",
        required: false,
        helperText: "Optional link — reviewer name becomes a purple link",
      },
      {
        name: "reviewDate",
        type: "string",
        required: false,
        helperText: "Display date (e.g., April 2026)",
      },
    ],
  },
];
